import { GroupRegistrationsCountsQuery } from "#src/api/GroupRegistration.ts";
import { UpdateOrganizationMutation } from "#src/api/Organization.ts";
import { ReferralTemplatePayload } from "#src/api/ReferralTemplate.ts";
import { StripeAccountsQuery } from "#src/api/Stripe.ts";
import { BorderButton } from "#src/components/buttons/BorderButton.tsx";
import { BorderCard } from "#src/components/items/BorderCard.tsx";
import ManageIcon from "#src/components/items/ManageIcon.tsx";
import { TerminalBanner } from "#src/components/items/TerminalBanner.tsx";
import Container from "#src/components/layout/Container.tsx";
import Loading from "#src/components/layout/Loading.tsx";
import Section from "#src/components/layout/Section.tsx";
import { AdyenActionBanner } from "#src/components/organizations/AdyenActionBanner.tsx";
import { OrganizationPaymentAlert } from "#src/components/organizations/OrganizationPaymentAlert.tsx";
import { UserReferralTemplate } from "#src/components/referrals/UserReferralTemplate.tsx";
import { Link } from "#src/components/Router.tsx";
import { SettingOnboardsProgress } from "#src/components/settings/SettingOnboardProgress.tsx";
import { OrganizationModel } from "#src/models/OrganizationModel.ts";
import { apiClient, useApi } from "#src/stores/Api.tsx";
import { useSession } from "#src/stores/Session.tsx";
import { useStore } from "#src/stores/Store.tsx";
import colors from "#src/utils/colors.ts";
import {
  ClassTypes,
  PrivateOfferingTypes,
  dateFormat,
} from "#src/utils/constants.ts";
import { useCurrentTimestamp } from "#src/utils/currentDate.ts";
import { moment } from "#src/utils/dateTime.ts";
import formatMoney from "#src/utils/formatMoney.ts";
import helmet from "#src/utils/helmet.ts";
import tw from "#src/utils/tw.ts";
import {
  BetaAccessType,
  CustomerServiceItemType,
  EventType,
  GroupRegistrationStatus,
  SortOrder,
  StripeType,
  manageAttendancesCounts,
  manageAttendancesCountsVariables,
  manageCustomerServiceItems,
  manageCustomerServiceItemsVariables,
  managePrivateSerieAttendancesCounts,
  nextAttendance,
  nextAttendanceVariables,
  nextPrivateSerieAttendance,
  nextPrivateSerieAttendanceVariables,
  updateOrganizationVariables,
  userReferralTemplate,
  userReferralTemplateVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import { groupBy, uniqBy } from "es-toolkit/compat";
import gql from "graphql-tag";
import { useEffect } from "react";

interface Props {}

export default function AdminLanding(_props: Props) {
  const currentTimestamp = useCurrentTimestamp();

  const store = useStore();
  const session = useSession();
  const today = moment(currentTimestamp);
  const date = new Date(today.format("YYYY-MM-DD"));

  const stripeAccountsQuery = useApi(StripeAccountsQuery, {
    variables: {
      organizationId: store.organization.id,
    },
  });

  useEffect(() => {
    helmet.setPageTitle("Admin");
  }, []);

  useEffect(() => {
    if (stripeAccountsQuery.stripeAccounts) {
      const hasAlert = stripeAccountsQuery.stripeAccounts.some(
        (r) => r.verified === false
      );

      if (hasAlert && !store.organization.stripeAlert) {
        store.setOrganization(
          new OrganizationModel({
            ...store.organization,
            stripeAlert: true,
          })
        );
      }
    }
  }, [stripeAccountsQuery]);

  const nextAttendanceVariables: nextAttendanceVariables = {
    where: {
      active: true,
      date: {
        gt: date,
      },
      organizationId: store.organization.id,
    },
    orderBy: {
      date: SortOrder.asc,
    },
    take: 1,
  };

  if (store.organization.hasDepartments) {
    nextAttendanceVariables.where.class = {
      departmentId: {
        in: session.departments.map((d) => d.id),
      },
    };
  }

  const queryNextAttendance = useApi(NextAttendanceQuery, {
    variables: nextAttendanceVariables,
  });

  const nextPrivateSerieAttendanceVariables: nextPrivateSerieAttendanceVariables =
    {
      where: {
        date: {
          gt: date,
        },
        organizationId: store.organization.id,
      },
      orderBy: {
        date: SortOrder.asc,
      },
      take: 1,
    };

  if (store.organization.hasDepartments) {
    nextPrivateSerieAttendanceVariables.where.privateSerie = {
      offering: {
        departmentId: {
          in: session.departments.map((d) => d.id),
        },
      },
    };
  }

  const queryNextPrivateSerieAttendance = useApi(
    NextPrivateSerieAttendanceQuery,
    {
      variables: nextPrivateSerieAttendanceVariables,
    }
  );

  const queryCustomerServiceItems = useApi(CustomerServiceItemsQuery, {
    variables: {
      where: {
        organizationId: store.organization.id,
        unread: true,
        type: {
          in: [
            CustomerServiceItemType.OutstandingBalance,
            CustomerServiceItemType.NoPaymentInformation,
            CustomerServiceItemType.ExpiredCard,
          ],
        },
      },
    },
  });

  const queryAttendancesCounts = useApi(AttendancesCountsQuery);

  const queryPrivateSerieAttendancesCounts = useApi(
    PrivateSerieAttendancesCountsQuery
  );

  const queryWaitlistRegistrationsCounts = useApi(
    GroupRegistrationsCountsQuery,
    {
      variables: {
        where: {
          organizationId: store.organization.id,
          status: {
            equals: GroupRegistrationStatus.Waitlist,
          },
        },
      },
    }
  );

  function renderProgramming() {
    let summary = `${store.unreadItems.inProgressClasses?.toLocaleString()} in-progress • ${store.unreadItems.upcomingClasses?.toLocaleString()} upcoming • ${store.unreadItems.enrolledParticipants.toLocaleString()} enrolled`;

    if (
      store.unreadItems.allClasses === 0 &&
      store.unreadItems.allOfferings === 0
    ) {
      summary = "Create your first offering";
    }

    return (
      <BorderCard to="/admin/group-offerings">
        <div className="flex items-center">
          <ManageIcon>
            <i aria-hidden className="far fa-sitemap" />
          </ManageIcon>
          <div className="flex-1 overflow-hidden pl-4">
            <p className="mb-0">
              <strong>Programming</strong>
            </p>
            <p className="text-empty mb-0 block truncate text-sm">{summary}</p>
          </div>
        </div>
      </BorderCard>
    );
  }

  function renderPrivateLessons() {
    const pending =
      store.unreadItems.pendingPrivateSeries +
      store.unreadItems.postedPrivateSeries;

    return (
      <BorderCard
        to={
          store.unreadItems.allOfferings > 0
            ? "/admin/custom-private-lessons"
            : "/admin/first-offering"
        }
      >
        <div className="flex items-center">
          <ManageIcon>
            <i aria-hidden className="far fa-clipboard-list-check" />
          </ManageIcon>
          <div className="flex-1 pl-4">
            <p className="mb-0">
              <strong>Custom {store.organization.privateTerminology}</strong>
            </p>
            <p className="text-empty mb-0 text-sm">
              {pending} pending • {store.unreadItems.inProgressPrivateSeries}{" "}
              in-progress
            </p>
          </div>
          {store.unreadItems.allOfferings === 0 && (
            <BorderButton type="submit">Get Started</BorderButton>
          )}
        </div>
      </BorderCard>
    );
  }

  function renderMemberships() {
    const allMembershipOfferings = store.unreadItems.allMembershipOfferings;
    const inProgressMembershipOfferings =
      store.unreadItems.inProgressMembershipOfferings;

    return (
      <BorderCard
        to={
          allMembershipOfferings > 0
            ? "/admin/membership-offerings"
            : "/admin/first-offering"
        }
      >
        <div className="flex items-center">
          <ManageIcon>
            <i aria-hidden className="far fa-certificate" />
          </ManageIcon>
          <div className="flex-1 overflow-hidden pl-4">
            <div className="text-dark flex items-center gap-2">
              <strong>Memberships</strong>
            </div>
            <p className="text-empty mb-0 block truncate text-sm">{`${inProgressMembershipOfferings} active`}</p>
          </div>
          {allMembershipOfferings === 0 && (
            <BorderButton type="submit">Get Started</BorderButton>
          )}
        </div>
      </BorderCard>
    );
  }

  function renderMeets() {
    const inProgressMeets = store.unreadItems.inProgressMeets || 0;
    const upcomingMeets = store.unreadItems.upcomingMeets || 0;

    return (
      <BorderCard to={"/admin/meets"}>
        <div className="flex items-center">
          <ManageIcon>
            <i aria-hidden className="far fa-person-swimming" />
          </ManageIcon>
          <div className="flex-1 overflow-hidden pl-4">
            <div className="text-dark flex items-center gap-2">
              <strong>Meets & Times</strong>
            </div>
            <p className="text-empty mb-0 block truncate text-sm">
              {inProgressMeets.toLocaleString()} in progress •{" "}
              {upcomingMeets.toLocaleString()} upcoming
            </p>
          </div>
        </div>
      </BorderCard>
    );
  }

  function renderBillingAlerts() {
    const customerServiceItems =
      queryCustomerServiceItems.customerServiceItems || [];

    const groupedCustomerServiceItems = groupBy(
      customerServiceItems,
      (i) => i?.user?.id
    );

    const unreadCustomerServiceItems = Object.keys(
      groupedCustomerServiceItems
    ).length;

    let outstandingBalanceItems = customerServiceItems.filter(
      (c) =>
        c.type === CustomerServiceItemType.OutstandingBalance ||
        c.type === CustomerServiceItemType.NoPaymentInformation
    );

    outstandingBalanceItems = uniqBy(outstandingBalanceItems, "user.id");

    const outstandingBalance = outstandingBalanceItems.reduce(
      (acc, c) => acc + Math.max(c.user?.balance, 0),
      0
    );

    return (
      <BorderCard className="overflow-hidden">
        <div>
          {store.organization.stripe !== StripeType.Adyen &&
            !store.organization.stripeAccountId &&
            !store.organization.stripeExpressAccountId && (
              <Link
                to="/admin/bank"
                className="bg-error -mx-4 -mt-4 mb-2 flex items-center gap-2 px-4 pt-2.5 pb-1.5 text-sm text-white"
              >
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
                  <i
                    aria-hidden
                    className="far fa-triangle-exclamation text-error"
                  />
                </div>
                <div>
                  Bank not connected. Tap here to start collecting payments.
                </div>
              </Link>
            )}
          <Link to="/admin/outstanding-balances">
            <div className="flex">
              <ManageIcon error={unreadCustomerServiceItems > 0}>
                <i
                  aria-hidden
                  className={tw(
                    "far",
                    unreadCustomerServiceItems > 0
                      ? "fa-circle-exclamation text-error"
                      : "fa-check"
                  )}
                />
              </ManageIcon>
              <div className="flex-1 pl-4">
                <p className="mb-0">
                  <strong>
                    {unreadCustomerServiceItems === 0
                      ? "No"
                      : unreadCustomerServiceItems.toLocaleString()}{" "}
                    Billing Alert{unreadCustomerServiceItems !== 1 && "s"}
                  </strong>
                </p>
                <p className="text-empty mb-0 text-sm">
                  {unreadCustomerServiceItems} account
                  {unreadCustomerServiceItems !== 1 && "s"} • $
                  {formatMoney(outstandingBalance)} due
                  {unreadCustomerServiceItems === 0 && " • Woohoo!"}
                </p>
              </div>
            </div>
          </Link>
        </div>
      </BorderCard>
    );
  }

  function renderToday() {
    const orgHasOfferings =
      store.unreadItems.allClasses > 0 || store.unreadItems.allOfferings > 0;

    if (!orgHasOfferings) {
      return null;
    }

    const attendancesCounts =
      queryAttendancesCounts.attendancesCounts?.today || {};
    const privateSerieAttendancesCounts =
      queryPrivateSerieAttendancesCounts.privateSerieAttendancesCounts?.today ||
      {};
    const hasItemsToday =
      Object.keys(attendancesCounts).length > 0 ||
      Object.keys(privateSerieAttendancesCounts).length > 0;
    const today = moment();
    let nextActivity: Date;
    let countdown = 0;

    const nextAttendance = queryNextAttendance.attendances[0];
    const nextPrivateSerieAttendance =
      queryNextPrivateSerieAttendance.privateSerieAttendances[0];
    const hasVisitedToday = store.organization.events.find(
      (e) => e.type === EventType.Visited && e.value === "Today"
    );

    const counts = {};
    const allTypes = { ...ClassTypes, ...PrivateOfferingTypes };

    for (const t in allTypes) {
      counts[t] = attendancesCounts[t] || 0;
    }

    for (const t in PrivateOfferingTypes) {
      counts[t] = privateSerieAttendancesCounts[t] || 0;
    }

    const summaries = [];

    for (const k in counts) {
      if (counts[k] === 0) {
        continue;
      }

      if (counts[k] > 1 && k === ClassTypes.Class) {
        summaries.push(`${counts[k]} ${k}es`);
      } else if (counts[k] > 1) {
        summaries.push(`${counts[k]} ${k}s`);
      } else if (counts[k] === 1) {
        summaries.push(`${counts[k]} ${k}`);
      }
    }

    if (summaries.length === 0) {
      summaries.push("Nothing today");

      if (nextAttendance) {
        summaries.push(
          `Next activity ${moment.utc(nextAttendance.date).format(dateFormat)}`
        );
        nextActivity = nextAttendance.date;
      } else if (nextPrivateSerieAttendance) {
        summaries.push(
          `Next activity ${moment
            .utc(nextPrivateSerieAttendance.date)
            .format(dateFormat)}`
        );
        nextActivity = nextPrivateSerieAttendance.date;
      } else {
        summaries.push("Nothing upcoming");
      }
    }

    if (nextActivity) {
      const today = moment().utc().startOf("day");
      countdown = moment(nextActivity).diff(today, "days");
    }

    const highlightToday =
      hasItemsToday &&
      ((hasVisitedToday &&
        moment(hasVisitedToday.createdAt).isSame(today, "day")) ||
        !hasVisitedToday);

    return (
      <div>
        <BorderCard
          style={{
            background: highlightToday
              ? `linear-gradient(120deg, #fff 0%, #fff 85%, ${colors.main} 85%, ${colors.main} 100%);`
              : undefined,
          }}
          to="/admin/schedule"
          onClick={async () => {
            if (hasItemsToday && !hasVisitedToday) {
              const variables: updateOrganizationVariables = {
                data: {
                  events: {
                    create: [
                      {
                        type: EventType.Visited,
                        value: "Today",
                      },
                    ],
                  },
                },
                where: {
                  id: store.organization.id,
                },
              };

              await apiClient(UpdateOrganizationMutation, { variables });
              await store.updateOrganization();
            }
          }}
        >
          <div className="flex items-center">
            <ManageIcon>
              <i aria-hidden className="far fa-calendar-check" />
            </ManageIcon>
            <div className="flex-1 pl-4">
              <p className="mb-0">
                <strong>Today</strong>
              </p>
              <p className="text-empty mb-0 text-sm">{summaries.join(" • ")}</p>
            </div>

            {highlightToday && (
              <>
                <div className="bg-organization flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white">
                  <i className="far fa-party-horn text-white" />{" "}
                  <strong>It’s go time!</strong>
                </div>
              </>
            )}

            {countdown > 0 && !highlightToday && (
              <>
                <div className="flex items-center gap-2 py-2 text-sm">
                  <i className="far fa-alarm-clock text-organization" />{" "}
                  <strong>
                    {countdown.toLocaleString()} day{countdown !== 1 && "s"}{" "}
                    away
                  </strong>
                </div>
              </>
            )}
          </div>
        </BorderCard>
      </div>
    );
  }

  function renderAlerts() {
    const stripeAlert = store.organization.stripeAlert;
    const pending = store.unread;
    const waitlist =
      queryWaitlistRegistrationsCounts?.groupRegistrationsCounts?.count || 0;

    let content = [];

    if (stripeAlert) {
      let message = "Bank requires urgent attention";

      if (
        store.organization.stripe === StripeType.Express ||
        store.organization.stripe === StripeType.Adyen
      ) {
        message = "A bank connection requires urgent attention";
      }

      content.push(
        <Link
          to="/admin/bank"
          key="stripe"
          className="text-dark flex flex-1 items-center gap-2 px-4 py-4 transition hover:bg-white"
        >
          <div className="bg-error flex h-11 w-11 items-center justify-center rounded-full text-xl">
            <i className="far fa-triangle-exclamation text-white" />
          </div>
          <strong className="flex-1">{message}</strong>
        </Link>
      );
    }

    if (pending > 0) {
      content.push(
        <Link
          to="/admin/pending"
          key="pending"
          className="text-dark flex flex-1 items-center gap-2 px-4 py-4 transition hover:bg-white"
        >
          <div className="bg-organization flex h-11 w-11 items-center justify-center rounded-full text-lg font-bold text-white">
            {pending.toLocaleString()}
          </div>
          <strong className="flex-1">Pending registrations & requests</strong>
        </Link>
      );
    }

    if (waitlist > 0) {
      content.push(
        <Link
          to="/admin/pending?type=waitlistRegistrations"
          key="waitlist"
          className="text-dark flex flex-1 items-center gap-2 px-4 py-4 transition hover:bg-white"
        >
          <div className="border-borderGray text-dark flex h-11 w-11 items-center justify-center rounded-full border bg-white text-lg font-bold">
            {waitlist.toLocaleString()}
          </div>
          <strong className="flex-1">Waitlisted registrations</strong>
        </Link>
      );
    }

    if (content.length === 0) {
      return null;
    }

    return (
      <div className="divide-borderGray border-borderGray mb-8 flex divide-x border-y sm:mb-4 sm:flex-col sm:divide-x-0 sm:divide-y">
        {content}
      </div>
    );
  }

  const loading =
    !store.organization ||
    !queryWaitlistRegistrationsCounts.groupRegistrationsCounts ||
    !queryNextAttendance.attendances ||
    !queryNextPrivateSerieAttendance.privateSerieAttendances ||
    !queryCustomerServiceItems.customerServiceItems ||
    store.unreadItems.now === null;

  if (loading) {
    return (
      <Section>
        <Container>
          <Loading />
        </Container>
      </Section>
    );
  }

  const orgHasOfferings =
    store.unreadItems.allClasses > 0 ||
    store.unreadItems.allOfferings > 0 ||
    store.unreadItems.allMembershipOfferings > 0;
  let offerings = null;
  const todayButton = renderToday();
  const billingAlerts = renderBillingAlerts();
  const alerts = renderAlerts();
  let privateLessons = null;
  let memberships = null;
  let meets = null;
  const setup = store.organization.departments.every(
    (d) => d.settingOnboards.length >= 9
  );

  if (store.organization.betaAccess.includes(BetaAccessType.Programming)) {
    offerings = renderProgramming();
  }

  if (store.organization.betaAccess.includes(BetaAccessType.PrivateLessons)) {
    privateLessons = renderPrivateLessons();
  }

  if (store.organization.betaAccess.includes(BetaAccessType.Memberships)) {
    memberships = renderMemberships();
  }

  if (
    store.organization.betaAccess.includes(BetaAccessType.Meets) &&
    (!store.organization.hasDepartments ||
      session.departments.some((d) => d.meetAccess))
  ) {
    meets = renderMeets();
  }

  const registrationButtons = (
    <>
      <BorderButton
        bgColor="#fff"
        className="text-organization flex-1 hover:border-[#ccc]"
        to="/admin/users/add?returnPathname=/admin"
      >
        Add New Account
      </BorderButton>
      <BorderButton
        bgColor="#fff"
        disabled={!orgHasOfferings}
        className="text-organization flex-1 hover:border-[#ccc]"
        to="/admin/find"
      >
        Start Registration
      </BorderButton>
    </>
  );

  return (
    <>
      <div
        className="absolute top-0 right-0 bottom-0 left-0"
        style={{
          background: `linear-gradient(to bottom, ${colors.paleAction}, #fff 400px)`,
        }}
      />
      <div>
        <Container large className="pt-8 pb-12">
          <div className="mb-4 flex items-center sm:mb-2">
            <h1 className="mb-0 flex-1 sm:mb-6 sm:text-xl">
              <strong>Hey {store.user.firstName}! Here’s the latest.</strong>
            </h1>

            <div className="flex gap-4 sm:hidden">{registrationButtons}</div>
          </div>

          <AdyenActionBanner />

          <OrganizationPaymentAlert />

          <SettingOnboardsProgress />

          {!orgHasOfferings && (
            <div className="border-borderGray flex items-center justify-center rounded-md border border-b-3 bg-white px-4 py-20">
              <BorderButton type="submit" to="/admin/first-offering">
                Create your first registration offering
              </BorderButton>
            </div>
          )}

          {orgHasOfferings && (
            <div className="mb-4 space-y-2">
              {alerts}

              <div className="hidden gap-4 sm:flex sm:pb-3">
                {registrationButtons}
              </div>

              {todayButton}
              {offerings}
              {privateLessons}
              {memberships}
              {meets}
              {billingAlerts}
            </div>
          )}

          <UserReferralTemplate />

          <TerminalBanner />

          {!setup && (
            <div className="py-8 text-center text-sm">
              <i className="far fa-circle-question text-organization" />{" "}
              <strong>Need help?</strong> Click the blue chat bubble in the
              corner.
            </div>
          )}
        </Container>
      </div>
    </>
  );
}

export const CustomerServiceItemsQuery = gql`
  query manageCustomerServiceItems($where: CustomerServiceItemWhereInput!) {
    customerServiceItems(where: $where) {
      id
      type
      user {
        id
        balance
      }
    }
  }
` as TypedDocumentNode<
  manageCustomerServiceItems,
  manageCustomerServiceItemsVariables
>;

const AttendancesCountsQuery = gql`
  query manageAttendancesCounts {
    attendancesCounts {
      today
    }
  }
` as TypedDocumentNode<
  manageAttendancesCounts,
  manageAttendancesCountsVariables
>;

const PrivateSerieAttendancesCountsQuery = gql`
  query managePrivateSerieAttendancesCounts {
    privateSerieAttendancesCounts {
      today
    }
  }
` as TypedDocumentNode<
  managePrivateSerieAttendancesCounts,
  manageAttendancesCountsVariables
>;

const NextAttendanceQuery = gql`
  query nextAttendance(
    $where: AttendanceWhereInput!
    $take: Int
    $orderBy: AttendanceOrderByInput
  ) {
    attendances(where: $where, take: $take, orderBy: $orderBy) {
      id
      date
      class {
        id
        type
      }
    }
  }
` as TypedDocumentNode<nextAttendance, nextAttendanceVariables>;

const NextPrivateSerieAttendanceQuery = gql`
  query nextPrivateSerieAttendance(
    $where: PrivateSerieAttendanceWhereInput!
    $take: Int
    $orderBy: PrivateSerieAttendanceOrderByInput
  ) {
    privateSerieAttendances(where: $where, take: $take, orderBy: $orderBy) {
      id
      date
      privateSerie {
        id
        offering {
          id
          type
        }
      }
    }
  }
` as TypedDocumentNode<
  nextPrivateSerieAttendance,
  nextPrivateSerieAttendanceVariables
>;

export const UserReferralTemplateQuery = gql`
  query userReferralTemplate($where: UserReferralTemplateWhereInput!) {
    userReferralTemplate(where: $where) {
      status
      referralTemplate {
        ...ReferralTemplatePayload
      }
    }
  }
  ${ReferralTemplatePayload}
` as TypedDocumentNode<userReferralTemplate, userReferralTemplateVariables>;
